import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  output,
  DestroyRef,
  signal
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AuviousRtcService,
  CompositionService,
  debug,
  debugError
} from "../../services";
import {
  CompositionNotification,
  IComposition,
  PageableRequestBuilder
} from "../../models";
import { ActivityIndicatorService } from "../../services/activity-indicator.service";
import { NotificationService } from "../../services/notification.service";
import { Event, PagedCollection } from "@auvious/common";
import {
  debounceTime,
  filter,
  firstValueFrom,
  groupBy,
  map,
  mergeMap
} from "rxjs";

@Component({
  selector: "app-composition-exports",
  templateUrl: "./composition-exports.component.html",
  styleUrls: ["./composition-exports.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CompositionExportsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private activityService = inject(ActivityIndicatorService);
  private compositionService = inject(CompositionService);
  private alertService = inject(NotificationService);
  private cd = inject(ChangeDetectorRef);

  historyLoading = signal(false);
  historyPager = signal<PagedCollection<IComposition>>(null);
  historyData = signal<IComposition[]>([]);
  currentPage = signal(0);

  readonly navigate = output<string>();

  constructor() {
    const rtc = inject(AuviousRtcService);

    firstValueFrom(rtc.getEventObservableAvailable()).then(
      (eventObservable) => {
        eventObservable
          .pipe(
            filter(
              (event) =>
                !!event.payload &&
                event.payload.type === "CompositionStateChangedEvent"
            ),
            map((event) => {
              debug("CompositionStateChangedEvent", event);
              return new CompositionNotification(event);
            }),
            groupBy((n) => n.getCompositionId()),
            mergeMap((group$) => group$.pipe(debounceTime(500))),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe((n) => this.processCompositionEvent(n));
      }
    );
  }

  async ngOnInit() {
    await this.nextPageLoading(0);
  }

  private async processCompositionEvent(n: CompositionNotification) {
    const compositionId = n.getCompositionId();
    const existing = this.historyData().find((c) => c.id === compositionId);

    if (existing) {
      try {
        const updated = await this.compositionService.getComposition(
          compositionId
        );
        this.historyData.update((history) =>
          history.map((c) => (c.id === compositionId ? updated : c))
        );
      } catch (ex) {
        debugError(ex);
      }
      return;
    }

    if (this.currentPage() === 0) {
      await this.nextPageLoading(0);
    } else if (n.canShow()) {
      this.alertService.notify(n);
    }
  }

  async nextPageLoading(page: number) {
    this.activityService.loading(true);
    const data = await this.nextPage(page);
    if (data) {
      this.historyData.set(data);
    }
  }

  async nextPage(page: number): Promise<IComposition[]> {
    this.currentPage.set(page);
    const pageable = new PageableRequestBuilder()
      .page(page)
      .size(10)
      .sort("created,desc")
      .build();
    this.historyLoading.set(true);
    try {
      const pager = await this.compositionService.query(pageable);
      this.historyPager.set(pager);
      return pager.content;
    } catch (ex) {
      this.alertService.error(ex.message || ex);
      debugError(ex);
      return null;
    } finally {
      this.activityService.loading(false);
      this.historyLoading.set(false);
    }
  }

  go(conversationId) {
    this.navigate.emit(conversationId);
  }

  removed(composition: IComposition) {
    // drop it here, a refetch right after the delete can still return it
    this.historyData.update((history) =>
      history.filter((c) => c.id !== composition.id)
    );
    if (this.historyData().length === 0 && this.currentPage() > 0) {
      this.nextPageLoading(this.currentPage() - 1);
    }
  }

  trackByFn(index: number, el: IComposition) {
    return el.id + el.state;
  }

  async cancelExport(composition: IComposition) {
    try {
      this.activityService.loading(true);
      await this.compositionService.cancelComposition(
        composition.conversationId,
        composition.id
      );
    } catch (ex) {
      this.alertService.error(ex.message || ex);
    } finally {
      this.activityService.loading(false);
    }
  }
}
